import axios from "axios";
import { UserResponse } from "@/api/auth/types";
import { useAuthStore } from "@/stores/auth-store";

// 401 for an invalid, expired or revoked refresh token, 400 for a missing one.
// Anything else (offline, 5xx, CORS) says nothing about the session.
const isSessionRejection = (error: unknown): boolean => {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;

  return status === 400 || status === 401;
};

class RefreshService {
  private static instance: RefreshService;
  private refreshPromise: Promise<void> | null = null;

  private constructor() {}

  public static getInstance(): RefreshService {
    if (!RefreshService.instance) {
      RefreshService.instance = new RefreshService();
    }
    return RefreshService.instance;
  }

  public async refreshTokenIfNeeded(): Promise<void> {
    if (!useAuthStore.getState().shouldRefreshToken()) {
      return;
    }

    try {
      await this.runRefresh();
    } catch (error) {
      if (isSessionRejection(error)) throw error;

      // Transient: go out on the current token and try again on the next request.
    }
  }

  private runRefresh(): Promise<void> {
    // Single-flight: the refresh rotates the token, so a second concurrent call
    // would be rejected.
    this.refreshPromise ??= this.refresh().finally(() => {
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }

  private async refresh(): Promise<void> {
    const { session, setSession, clearSession } = useAuthStore.getState();
    if (!session) return;

    try {
      // Plain axios: APIClient's request interceptor is what called us.
      const response = await axios.post<UserResponse>(
        `${import.meta.env.VITE_API_BASE_URL}/refresh`,
        {
          refreshToken: session.refreshToken,
          client_id: import.meta.env.VITE_CLIENT_ID,
        },
      );

      setSession(response.data.session);
    } catch (error) {
      if (isSessionRejection(error)) clearSession();

      throw error;
    }
  }
}

export default RefreshService;
